// src/components/AoiPanel.jsx
//
// Migrated from AoiToolsPage / AoiPanel standalone page.
// Mounted by MapPage in the left panel slot when mapMode === 'aoi'.
//
// Receives drawInstance + drawVersion from MapPage so it can
// read draw state (lastFeature) for the save flow.
//
// Day 9 row semantics:
//   - Row body click → select + zoom (no edit mode entry)
//   - Spline icon → enter polygon (vertex) edit mode (disabled when
//                   AOI has no geometry yet)
//   - Pencil icon → rename inline
//   - Trash icon → delete confirm inline
//   - LocateFixed icon retired — zoom is now on row click
//
// The AoiToolbar is mounted by MapPage as a floating map overlay
// (top-left, z-30) — NOT by this panel. It operates on the shared
// MlDraw instance and exposes draw + transform + vertex-edit modes.
//
// Operating AOIs are NOT shown here. They are rendered on the map
// as a read-only reference ring via useOperatingAoiContext.

import { useEffect, useRef, useState } from "react";
import { Loader2, Pencil, Trash2, Plus, Spline, ArrowLeft, X } from "lucide-react";
import { useAoiStore } from "../stores/useAoiStore";
import { useProjectStore } from "../stores/useProjectStore";
import { useMapActions } from "../stores/useMapActions";
import { useAppNav } from "../hooks/useAppNav";
import { aoiBoundsFlat } from "../utils/aoiBounds";

export default function AoiPanel({ drawInstance, drawVersion }) {
  const {
    aois,
    loading,
    activeAoiId,
    aoiPolygonEditMode,
    aoiEditorEntryIntent,
    dirtyAoiId,
    hoveredAoiId,
    loadAois,
    createAoi,
    deleteAoi,
    updateAoi,
    selectAoi,
    setActiveAoi,
    setHoveredAoi,
    enterAoiPolygonEdit,
    exitAoiPolygonEdit,
    clearEditorEntryIntent,
    clearDirtyAoi,
  } = useAoiStore();
  const { setMapMode } = useAppNav();

  const { activeProjectId, isGlobal, projects } = useProjectStore();

  const zoomToBounds = useMapActions((s) => s.zoomToBounds);

  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newAoiName, setNewAoiName] = useState("");
  const [createError, setCreateError] = useState("");

  // Day 9 Path A — focus + highlight on "+ New" arrival. AoisTab sets
  // aoiEditorEntryIntent='new' before flipping mapMode; we focus the
  // input on the next paint and run a ~2s amber highlight ring. The
  // intent flag is consumed once (cleared immediately) so re-renders
  // don't re-trigger.
  const nameInputRef = useRef(null);
  const [entryHighlight, setEntryHighlight] = useState(false);

  useEffect(() => {
    if (aoiEditorEntryIntent === "new") {
      // Wait a paint for the panel's opacity transition to settle.
      requestAnimationFrame(() => {
        nameInputRef.current?.focus();
      });
      setEntryHighlight(true);
      const t = setTimeout(() => setEntryHighlight(false), 2000);
      clearEditorEntryIntent();
      return () => clearTimeout(t);
    }
    if (aoiEditorEntryIntent === "manage") {
      // Just clear the flag; no input emphasis on Manage arrival.
      clearEditorEntryIntent();
    }
  }, [aoiEditorEntryIntent, clearEditorEntryIntent]);

  // Day 9 Path A — Back to Layers exit. Discard prompt gates the
  // navigation when there are unsaved polygon edits. `dirtyAoiId`
  // is set by AoiToolbar's onUpdate hook (compares against the
  // snapshot taken at edit-mode entry).
  function handleBackToLayers() {
    if (dirtyAoiId && activeAoiId && dirtyAoiId === activeAoiId) {
      const ok = window.confirm(
        "You have unsaved polygon edits. Discard them and return to Layers?"
      );
      if (!ok) return;
    }
    // Discarding the edit state — also clear the dirty flag and the
    // vertex-edit mode so the next entry to the editor is clean.
    if (aoiPolygonEditMode) exitAoiPolygonEdit();
    if (dirtyAoiId) clearDirtyAoi();
    setMapMode("layers");
  }

  const safeAois = Array.isArray(aois) ? aois : [];

  /* ── Load AOIs when project changes ────────────────────────────────── */

  useEffect(() => {
    if (!activeProjectId || isGlobal) return;
    loadAois(activeProjectId);
  }, [activeProjectId, isGlobal, loadAois]);

  /* ── No project selected ────────────────────────────────────────────── */

  if (isGlobal || !activeProjectId) {
    return (
      <div className="flex flex-col h-full">
        <PanelHeader onBack={handleBackToLayers} />
        <div className="flex-1 flex items-center justify-center px-4">
          <p className="text-xs text-fg-secondary text-center leading-relaxed">
            Select a project to manage<br />area of interest boundaries.
          </p>
        </div>
      </div>
    );
  }

  /* ── Create new AOI ─────────────────────────────────────────────────── */

  async function handleCreate() {
    const name = newAoiName.trim();
    if (!name) {
      setCreateError("Name is required.");
      return;
    }

    setCreating(true);
    setCreateError("");

    try {
      const created = await createAoi({
        name,
        description: "",
        geom: null, // user draws after creation
      });

      if (!created) throw new Error("Creation failed");

      setNewAoiName("");
      setCreating(false);
      // Selecting the new AOI triggers AoiToolbar draw tools to activate
      setActiveAoi(created.id);
    } catch (err) {
      setCreateError(err?.message || "Failed to create AOI.");
      setCreating(false);
    }
  }

  /* ── Rename ─────────────────────────────────────────────────────────── */

  async function handleRename(id) {
    const name = draftName.trim();
    if (!name) return;
    await updateAoi(id, { name });
    setEditingId(null);
  }

  /* ── Delete ─────────────────────────────────────────────────────────── */

  async function handleDelete(id) {
    await deleteAoi(id);
    setDeleteConfirmId(null);
  }

  /* ── Render ─────────────────────────────────────────────────────────── */

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PanelHeader />

      {/* ── Create form ── */}
      <div className="px-3 py-2 border-b border-slate-800">
        <div className="flex gap-1.5">
          <input
            type="text"
            ref={nameInputRef}
            value={newAoiName}
            onChange={(e) => {
              setNewAoiName(e.target.value);
              if (createError) setCreateError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
              if (e.key === "Escape") { setNewAoiName(""); setCreateError(""); }
            }}
            placeholder="New AOI name…"
            maxLength={80}
            className={[
              "flex-1 min-w-0 rounded bg-bg px-2.5 py-1.5 text-xs text-white",
              "placeholder-slate-600 outline-none border transition-all",
              entryHighlight
                ? "border-amber-400/80 ring-2 ring-amber-400/40 shadow-[0_0_8px_rgba(245,158,11,0.4)]"
                : createError
                ? "border-red-500/60 focus:ring-1 focus:ring-red-500/30"
                : "border-slate-700 focus:border-accent-info/50 focus:ring-1 focus:ring-accent-info/20",
            ].join(" ")}
          />
          <button
            onClick={handleCreate}
            disabled={creating || !newAoiName.trim()}
            title="Create AOI"
            className={[
              "flex items-center justify-center w-[30px] h-[30px] rounded border transition-colors shrink-0",
              creating || !newAoiName.trim()
                ? "border-slate-800 bg-bg text-slate-700 cursor-not-allowed"
                : "border-accent-action/40 bg-accent-action/10 text-accent-action hover:bg-accent-action/20",
            ].join(" ")}
          >
            {creating
              ? <Loader2 size={13} className="animate-spin" />
              : <Plus size={13} />
            }
          </button>
          {/* Cancel the new-AOI operation and return to the Layers panel
              (discard prompt gates this if there are unsaved polygon edits). */}
          <button
            type="button"
            onClick={handleBackToLayers}
            title="Cancel — back to Layers"
            className="flex items-center justify-center w-[30px] h-[30px] rounded border border-slate-700 bg-bg text-slate-400 hover:text-gray-100 hover:border-slate-500 transition-colors shrink-0"
          >
            <X size={13} />
          </button>
        </div>
        {createError && (
          <p className="mt-1 text-[10px] text-red-400">{createError}</p>
        )}
        {safeAois.length === 0 && !loading && (
          <p className="mt-1.5 text-[10px] text-fg-secondary leading-relaxed">
            Create an AOI, then draw its boundary on the map.
          </p>
        )}
      </div>

      {/* ── AOI list ── */}
      <div className="flex-1 overflow-y-auto py-2">
        {loading ? (
          <div className="flex justify-center pt-8">
            <Loader2 size={16} className="animate-spin text-fg-secondary" />
          </div>
        ) : safeAois.length === 0 ? (
          <p className="px-3 text-xs text-fg-secondary italic">No AOIs yet.</p>
        ) : (
          <ul className="flex flex-col gap-0.5 px-2">
            {safeAois.map((aoi) => {
              const isActive = activeAoiId === aoi.id;
              const isDirty = dirtyAoiId === aoi.id;
              const isHovered = hoveredAoiId === aoi.id;
              const isEditing = editingId === aoi.id;
              const isConfirmDelete = deleteConfirmId === aoi.id;
              const hasGeom = Boolean(aoi.geom);

              return (
                <li
                  key={aoi.id}
                  onMouseEnter={() => setHoveredAoi(aoi.id)}
                  onMouseLeave={() => setHoveredAoi(null)}
                  onClick={() => {
                    if (isEditing) return;
                    // Day 9 — row click = select + zoom. NO edit mode entry.
                    // Edit Polygon icon (Spline) is the polygon-edit affordance.
                    // Exiting any prior vertex-edit on selection change is a clean
                    // reset; the user can re-enter via the icon if desired.
                    if (aoiPolygonEditMode && aoiPolygonEditMode !== aoi.id) {
                      exitAoiPolygonEdit();
                    }
                    selectAoi(aoi.id);
                    if (hasGeom) {
                      const bounds = aoiBoundsFlat(aoi.geom);
                      if (bounds) zoomToBounds(bounds);
                    }
                  }}
                  className={[
                    "group rounded-md px-2.5 py-2 cursor-pointer transition-all border text-xs",
                    isActive
                      ? "border-accent-action/50 shadow-[0_0_10px_rgb(var(--accent)/0.18)]"
                      : isHovered
                      ? "bg-bg-soft border-slate-700"
                      : "bg-bg border-slate-800 hover:border-slate-700",
                  ].join(" ")}
                >
                  {isEditing ? (
                    /* ── Rename input ── */
                    <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        autoFocus
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRename(aoi.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        className="flex-1 min-w-0 rounded bg-bg border border-slate-700 px-2 py-0.5 text-xs text-white outline-none focus:border-accent-action/50"
                      />
                      <button
                        onClick={() => handleRename(aoi.id)}
                        className="text-[10px] text-accent-action hover:text-accent-action/80 font-medium"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="text-[10px] text-fg-secondary hover:text-slate-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : isConfirmDelete ? (
                    /* ── Delete confirm ── */
                    <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                      <span className="text-red-400 text-[11px]">Remove "{aoi.name}"?</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDelete(aoi.id)}
                          className="text-[10px] text-red-400 hover:text-red-300 font-medium"
                        >
                          Remove
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="text-[10px] text-fg-secondary hover:text-slate-300"
                        >
                          Keep
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── Normal row ── */
                    <div className="flex items-center gap-2">
                      {/* Geometry status dot */}
                      <span
                        title={hasGeom ? "Has boundary" : "No boundary drawn yet"}
                        className={[
                          "w-1.5 h-1.5 rounded-full shrink-0 transition-colors",
                          hasGeom
                            ? isActive
                              ? "bg-accent-action shadow-[0_0_4px_rgb(var(--accent))]"
                              : "bg-accent-action/50"
                            : "bg-slate-700",
                        ].join(" ")}
                      />

                      <span className={`flex-1 truncate font-medium ${isActive ? "text-accent-action" : "text-slate-200"}`}>
                        {aoi.name}
                        {isDirty && (
                          <span className="inline-block w-1 h-1 ml-1.5 rounded-full bg-accent-info align-middle" />
                        )}
                      </span>

                      {/* Actions — visible on hover or when active. Day 9:
                          LocateFixed retired (row click zooms now); Spline
                          added as "Edit polygon" — disabled when AOI has no
                          geom yet (draft must be drawn before vertex-edit). */}
                      <div className={`flex gap-1.5 transition-opacity ${isActive || isHovered ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!hasGeom) return;
                            // Enter polygon edit + select + zoom for visual focus.
                            const bounds = aoiBoundsFlat(aoi.geom);
                            if (bounds) zoomToBounds(bounds);
                            enterAoiPolygonEdit(aoi.id);
                          }}
                          disabled={!hasGeom}
                          title={hasGeom
                            ? (aoiPolygonEditMode === aoi.id
                                ? "Editing polygon vertices — click again to keep editing"
                                : "Edit polygon vertices")
                            : "Draw the polygon first"}
                          className={[
                            "transition-colors",
                            !hasGeom
                              ? "text-slate-800 cursor-not-allowed"
                              : aoiPolygonEditMode === aoi.id
                                ? "text-amber-400 hover:text-amber-300"
                                : "text-fg-secondary hover:text-accent-info",
                          ].join(" ")}
                        >
                          <Spline size={12} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(aoi.id);
                            setDraftName(aoi.name);
                          }}
                          title="Rename"
                          className="text-fg-secondary hover:text-slate-300 transition-colors"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(aoi.id);
                          }}
                          title="Delete"
                          className="text-fg-secondary hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Draw hint — show when selected but no geom */}
                  {isActive && !hasGeom && !isEditing && (
                    <p className="mt-1.5 text-[10px] text-fg-secondary leading-relaxed pl-3.5">
                      Use the draw tools above to set this AOI's boundary.
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Footer note ── */}
      <div className="px-3 py-2 border-t border-slate-800">
        <p className="text-[10px] text-fg-secondary leading-relaxed">
          AOIs define spatial context for this project.
          Select an AOI, draw its boundary, then save.
        </p>
      </div>
    </div>
  );
}

/* ── Panel header ───────────────────────────────────────────────────────── */

function PanelHeader({ onBack }) {
  return (
    <div className="px-3 py-2.5 border-b border-slate-800 flex items-center justify-between shrink-0">
      <div className="flex items-center gap-2">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            title="Back to Layers"
            className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-100 transition"
          >
            <ArrowLeft size={11} />
            Layers
          </button>
        )}
        <h2 className="text-[10px] font-semibold tracking-[0.14em] font-mono uppercase text-slate-400">
          Areas of Interest
        </h2>
      </div>
    </div>
  );
}